/**
 * Mikrotik radio-antenna catalog — point-to-point wireless bridges.
 *
 * Used for site-to-site CCTV backbones where running fibre is
 * impractical (across roads, between buildings on a campus, to a
 * remote pole). Catalog covers the 5 GHz long-range (LHG / SXTsq /
 * NetMetal) and 60 GHz short-range high-throughput families
 * (Wireless Wire, Cube 60Pro).
 *
 * Every entry verified against mikrotik.com — `datasheetUrl` + `// SRC:`
 * point at the actual page. Beam-width values are noted as either
 * vendor-stated or "industry-typical for {gain} dBi at {band}" when
 * the product page omits the spec.
 *
 * Compiled: 2026-05-15, release/v1.6.7-23k-catalog.
 */
import type { EquipmentCatalogEntry } from './equipmentCatalogEntry';

export const MIKROTIK_RADIO_ANTENNAS: EquipmentCatalogEntry[] = [
  // ═══════════════════════════════════════════════════════════════════════
  // 5 GHz long-range grid dishes — LHG family
  // ═══════════════════════════════════════════════════════════════════════

  // SRC: https://mikrotik.com/product/lhg_5_ac
  //      LHG 5 ac: 24.5 dBi dual-polarisation grid, 5150-5875 MHz int'l,
  //      867 Mbps 802.11ac dual-chain, 10-30 V passive PoE-in, 8 W max,
  //      IP54, -40..+70 °C, IPQ-4018 ARM quad 716 MHz, 256 MB RAM,
  //      single GbE. NOTE: marked discontinued on the product page,
  //      retained here because many existing installs still use it.
  {
    id: 'mikrotik-lhg-5-ac',
    manufacturer: 'Mikrotik',
    model: 'LHG 5 ac',
    category: 'radio-antenna',
    tagline:
      'LHG 5 ac — 24.5 dBi grid dish, 5 GHz, 867 Mbps 802.11ac, IP54, long-range PtP',
    verified: true,
    datasheetUrl: 'https://mikrotik.com/product/lhg_5_ac',
    specs: {
      band: '5 GHz',
      rangeM: 20000, // 5 GHz grid dishes typically deliver 20+ km LoS at this gain
      gainDbi: 24.5,
      beamWidthDeg: 7, // industry-typical for 24.5 dBi grid at 5 GHz
      throughputMbps: 867,
      standard: '802.11ac',
      mimo: '2×2',
      poeInVoltage: '10-30 V passive',
      ipRating: 'IP54',
      operatingTempC: '-40..+70',
      powerConsumptionW: 8,
    },
  },

  // SRC: https://mikrotik.com/product/lhg_xl_5_ac
  //      LHG XL 5 ac: 27 dBi grid (larger reflector than LHG 5 ac), 5 GHz,
  //      867 Mbps 802.11ac dual-chain, 10-30 V passive PoE-in, 8 W max,
  //      IP54, -40..+70 °C. Bridging 998 Mbps / routing 762 Mbps.
  {
    id: 'mikrotik-lhg-xl-5-ac',
    manufacturer: 'Mikrotik',
    model: 'LHG XL 5 ac',
    category: 'radio-antenna',
    tagline:
      'LHG XL 5 ac — 27 dBi extended-range grid, 5 GHz, 998 Mbps bridging, IP54',
    verified: true,
    datasheetUrl: 'https://mikrotik.com/product/lhg_xl_5_ac',
    specs: {
      band: '5 GHz',
      rangeM: 30000, // 27 dBi grid commonly hits 25-40 km LoS
      gainDbi: 27,
      beamWidthDeg: 5, // industry-typical for 27 dBi grid at 5 GHz
      throughputMbps: 998,
      standard: '802.11ac',
      mimo: '2×2',
      poeInVoltage: '10-30 V passive',
      ipRating: 'IP54',
      operatingTempC: '-40..+70',
      powerConsumptionW: 8,
    },
  },

  // ═══════════════════════════════════════════════════════════════════════
  // 5 GHz compact panels / sector-class — SXTsq family
  // ═══════════════════════════════════════════════════════════════════════

  // SRC: https://mikrotik.com/product/sxtsq_5_ac
  //      SXTsq 5 ac: 16 dBi integrated panel antenna, 5 GHz, 867 Mbps
  //      802.11ac dual-chain (2×2), 10-28 V passive PoE-in, 7 W max,
  //      IP55, -40..+70 °C, IPQ-4018 quad-core, 129×129×34 mm compact CPE.
  {
    id: 'mikrotik-sxtsq-5-ac',
    manufacturer: 'Mikrotik',
    model: 'SXTsq 5 ac',
    category: 'radio-antenna',
    tagline:
      'SXTsq 5 ac — 16 dBi compact panel CPE, 5 GHz, 867 Mbps 802.11ac, IP55',
    verified: true,
    datasheetUrl: 'https://mikrotik.com/product/sxtsq_5_ac',
    specs: {
      band: '5 GHz',
      rangeM: 5000, // mid-distance CPE — 5 km typical line of sight at this gain
      gainDbi: 16,
      beamWidthDeg: 25, // industry-typical for 16 dBi panel at 5 GHz
      throughputMbps: 867,
      standard: '802.11ac',
      mimo: '2×2',
      poeInVoltage: '10-28 V passive',
      ipRating: 'IP55',
      operatingTempC: '-40..+70',
      powerConsumptionW: 7,
    },
  },

  // ═══════════════════════════════════════════════════════════════════════
  // 60 GHz high-throughput short-range — Wireless Wire / Cube 60Pro
  // ═══════════════════════════════════════════════════════════════════════

  // SRC: https://mikrotik.com/product/wireless_wire_dish
  //      Wireless Wire Dish (LHGG-60ad kit): 60 GHz, up to 1500 m PtP,
  //      2 Gb/s aggregate paired throughput, AES-encrypted preconfigured
  //      kit, 802.3af/at PoE-in, 5 W max, IPQ-4019 quad ARM, 256 MB RAM,
  //      IP55, -40..+70 °C, Ø 391 × 222 mm. Ships as paired TX+RX kit.
  {
    id: 'mikrotik-wireless-wire-dish',
    manufacturer: 'Mikrotik',
    model: 'Wireless Wire Dish (LHGG-60ad kit)',
    category: 'radio-antenna',
    tagline:
      'Wireless Wire Dish — 60 GHz paired kit, 1.5 km, 2 Gb/s aggregate, AES, IP55',
    verified: true,
    datasheetUrl: 'https://mikrotik.com/product/wireless_wire_dish',
    specs: {
      band: '60 GHz',
      rangeM: 1500,
      gainDbi: 35, // 60 GHz dish — typical for 30+ cm reflector
      beamWidthDeg: 3, // narrow 60 GHz dish
      throughputMbps: 2000, // 2 Gb/s aggregate
      standard: '802.11ad',
      mimo: '1×1', // 60 GHz typically single-chain
      poeInVoltage: '802.3af/at',
      ipRating: 'IP55',
      operatingTempC: '-40..+70',
      powerConsumptionW: 5,
      pairedKit: true,
    },
  },

  // ═══════════════════════════════════════════════════════════════════════
  // High-power outdoor base station — NetMetal
  // ═══════════════════════════════════════════════════════════════════════

  // SRC: https://mikrotik.com/product/netmetal_ac2
  // SRC: https://help.mikrotik.com/docs/spaces/UM/pages/12484636/NetMetal+ac²
  //      NetMetal ac²: outdoor 2.4/5 GHz dual-band concurrent 802.11ac
  //      base station / PtMP AP, 4-core CPU, 256 MB RAM, miniPCIe slot
  //      for LTE / wireless module expansion, SFP fibre uplink, 2× RP-SMA
  //      external antenna connectors (BYO antenna for gain), IP66 outdoor
  //      enclosure, pole or DIN rail mount.
  {
    id: 'mikrotik-netmetal-ac2',
    manufacturer: 'Mikrotik',
    model: 'NetMetal ac²',
    category: 'radio-antenna',
    tagline:
      'NetMetal ac² — outdoor 2.4 + 5 GHz dual-concurrent base station, SFP + miniPCIe, IP66',
    verified: true,
    datasheetUrl: 'https://mikrotik.com/product/netmetal_ac2',
    specs: {
      band: '5 GHz',
      rangeM: 0, // depends on external antenna (BYO); range unrated for the radio alone
      gainDbi: 0, // external antenna — value depends on chosen antenna
      throughputMbps: 867,
      standard: '802.11ac',
      mimo: '2×2',
      ipRating: 'IP66',
      operatingTempC: '-40..+60',
    },
  },

  // SRC: https://mikrotik.com/product/cube_60pro_ac
  //      Cube 60Pro ac: 60 GHz 802.11ay PtP CPE, up to 2.4 km tested,
  //      backup 5 GHz radio (11.5 dBi, 433 Mbps) for adverse weather
  //      failover, 986 Mbps bridging at 1518 byte packets, 802.3af/at
  //      PoE-in 18-48 V, 10 W max, IP66, 115 × 95 × 82 mm. Available
  //      as preconfigured "Wireless Wire Cube Pro" paired kit.
  {
    id: 'mikrotik-cube-60pro-ac',
    manufacturer: 'Mikrotik',
    model: 'Cube 60Pro ac',
    category: 'radio-antenna',
    tagline:
      'Cube 60Pro — 60 GHz 802.11ay PtP, 2.4 km, 986 Mbps + 5 GHz weather backup, IP66',
    verified: true,
    datasheetUrl: 'https://mikrotik.com/product/cube_60pro_ac',
    specs: {
      band: '60 GHz',
      rangeM: 2400,
      gainDbi: 22, // 60 GHz compact dish; 5 GHz backup is 11.5 dBi
      beamWidthDeg: 5,
      throughputMbps: 986,
      standard: '802.11ay',
      mimo: '1×1',
      poeInVoltage: '802.3af/at (18-48 V)',
      ipRating: 'IP66',
      operatingTempC: '-40..+70',
      powerConsumptionW: 10,
      pairedKit: false, // single unit; paired kit is "Wireless Wire Cube Pro" — separate SKU
    },
  },
];
